import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { motion } from 'framer-motion';

const TRUNCATE_LIMIT = 160;
const SPEED_DESKTOP = 30; // px/s, como no carrossel do blog
const SPEED_MOBILE = 45;
const RETOMAR_MS = 2000;
const GAP = 24;

// Largura de uma cópia dos cards: inclui o espaço depois do último
const metadeDo = (trilho: HTMLElement) => (trilho.scrollWidth + GAP) / 2;
const GOOGLE_AVALIACOES = 'https://www.google.com/maps?cid=6868739132192685013';
const GOOGLE_AVALIAR = 'https://g.page/r/CdV7Kz3ZqVJfEBM/review';

const TestimonialsSection: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [montado, setMontado] = useState(false);
  const trilhoRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const pausadoRef = useRef(false);
  const retomarRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rolagem automática igual à do blog; rola o próprio trilho para o dedo continuar deslizando
  useEffect(() => {
    setMontado(true); // a cópia dos cards (para o laço) fica fora do HTML pré-renderizado
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    let ultimo: number | null = null;
    const tick = (ts: number) => {
      const trilho = trilhoRef.current;
      if (trilho && !pausadoRef.current) {
        if (ultimo === null) {
          posRef.current = trilho.scrollLeft;
        } else {
          const velocidade = window.innerWidth < 768 ? SPEED_MOBILE : SPEED_DESKTOP;
          posRef.current += (velocidade * (ts - ultimo)) / 1000;
          const metade = metadeDo(trilho);
          if (posRef.current >= metade) posRef.current -= metade;
          trilho.scrollLeft = posRef.current;
        }
        ultimo = ts;
      } else {
        ultimo = null;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (retomarRef.current) clearTimeout(retomarRef.current);
    };
  }, []);

  const pausar = () => {
    if (retomarRef.current) clearTimeout(retomarRef.current);
    pausadoRef.current = true;
  };

  const retomarDepois = () => {
    if (retomarRef.current) clearTimeout(retomarRef.current);
    retomarRef.current = setTimeout(() => {
      pausadoRef.current = false;
    }, RETOMAR_MS);
  };

  const rolar = (direcao: 1 | -1) => {
    const trilho = trilhoRef.current;
    const card = trilho?.firstElementChild as HTMLElement | null;
    if (!trilho || !card) return;
    pausar();
    const metade = metadeDo(trilho);
    const passo = card.offsetWidth + GAP;
    // No laço: salta para a posição equivalente na outra cópia antes de andar
    if (direcao === 1 && trilho.scrollLeft + passo >= metade) trilho.scrollLeft -= metade;
    if (direcao === -1 && trilho.scrollLeft - passo < 0) trilho.scrollLeft += metade;
    trilho.scrollBy({ left: direcao * passo, behavior: 'smooth' });
    retomarDepois();
  };

  const testimonials = [
    {
      name: 'Julia',
      text: 'oi td bem ? e a julia aqui de socorro vc me atendeu no PECA queria te agradecer muito por todo carinho atencao que vc me deu vc e incrivel que Deus te abencoe muito vc me trouxe muita paz depois que conversamos nao tive mais crise de choro , nervoso to muito mais feliz estou me cuidando meu marido tambem comecou a cuidar da saude dele que Deus abencoe cada dia mais nunca vou esquecer vc',
    },
    {
      name: 'Victoria Andrade',
      text: 'A Eliane é super atenciosa e sabe deixar a gente a vontade nas sessões, consegui me conectar com ela muito facilmente. Recomendo demaisss',
    },
    {
      name: 'Ana P.',
      text: 'O ambiente acolhedor e a forma como a Dra. Eliane conduz as sessoes me fizeram sentir segura para compartilhar minhas questoes mais profundas.',
    },
    // Avaliações do Google (texto como publicado)
    {
      name: 'Jéssica E.',
      text: 'Fazer terapia com a Psicóloga Eliane tem sido uma das decisões mais importantes e transformadoras da minha vida. Desde a primeira sessão, me senti acolhida, ouvida e respeitada. Ela tem uma escuta atenta, empática e, ao mesmo tempo, consegue trazer reflexões profundas de forma leve e cuidadosa. Gosto muito da maneira como conduz as sessões, sempre com profissionalismo, sensibilidade e sem julgamentos. Aos poucos, fui ganhando mais clareza sobre meus sentimentos, comportamentos e relações. Hoje me sinto mais segura, mais consciente de mim mesma e mais preparada para lidar com os desafios do dia a dia. Não largo mais ela por nada. Recomendo de olhos fechados para quem busca um espaço seguro, acolhedor e com um olhar verdadeiramente humano.',
    },
    {
      name: 'Rafael C.',
      text: 'Fiz acompanhamento com a psicóloga Eliane e tive uma experiência muito boa. Ela tem uma escuta muito atenta e conduz as sessões com respeito e sensibilidade. Ao longo do processo, consegui olhar para várias questões da minha vida com mais clareza e refletir melhor sobre algumas coisas que me incomodavam. Recomendo o trabalho dela para quem busca um espaço sério de escuta e reflexão.',
    },
    {
      name: 'Martha N.',
      text: 'Profissional incrível, acolhedora e muito humana no cuidado com seus pacientes. Seu olhar sensível faz toda a diferença no processo terapêutico.',
    },
    {
      name: 'Érica',
      text: 'Eliana é uma profissional acolhedora, que faz com que você consiga se sentir a vontade para interagir e contar sua história. A experiência têm sido agradável e impactante em minha vida.',
    },
    {
      name: 'Alexandre D.',
      text: 'Uma Psicóloga excelente, atenciosa, dedicada, parabéns pelo seu amor a profissão.',
    },
    {
      name: 'Eduarda',
      text: 'Excelente profissional, me senti acolhida desde o primeiro dia. 🥰🩵',
    },
  ];

  return (
    <motion.section
      id="depoimentos"
      className="py-20 bg-white"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-flex rounded-full bg-[#edf2fb] border border-[#d7deeb] px-4 py-2 text-xs tracking-[0.16em] uppercase text-[#2a3c5c] font-medium">
            Depoimentos
          </span>
          <motion.h2
            className="text-3xl sm:text-4xl font-semibold mt-5 mb-4 text-[#2a3c5c]"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            O que dizem sobre o meu trabalho
          </motion.h2>
        </div>
        <div className="relative group">
        <div
          ref={trilhoRef}
          className="flex gap-6 overflow-x-auto pt-4 pb-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Depoimentos (deslize para ver mais)"
          onMouseEnter={pausar}
          onMouseLeave={() => { if (expandedIndex === null) pausadoRef.current = false; }}
          onTouchStart={pausar}
          onTouchEnd={() => { if (expandedIndex === null) retomarDepois(); }}
        >
          {(montado ? [...testimonials, ...testimonials] : testimonials).map((testimonial, posicao) => {
            const index = posicao % testimonials.length;
            const isLong = testimonial.text.length > TRUNCATE_LIMIT;
            const isExpanded = expandedIndex === index;
            const displayText =
              isLong && !isExpanded
                ? testimonial.text.slice(0, TRUNCATE_LIMIT).trimEnd() + '...'
                : testimonial.text;

            return (
              <motion.article
                key={posicao}
                aria-hidden={posicao >= testimonials.length || undefined}
                className="shrink-0 w-[85%] sm:w-[340px] rounded-2xl border border-[#d7deeb] bg-white p-6 shadow-[0_10px_30px_rgba(26,43,73,0.08)] flex flex-col cursor-default"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{
                  y: -4,
                  boxShadow: '0 26px 58px rgba(26,43,73,0.18)',
                  borderColor: '#afc2dc',
                  transition: { duration: 0.25, ease: 'easeOut' },
                }}
                transition={{ duration: 0.5, delay: Math.min(index, 3) * 0.15 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center mb-4 gap-1">
                  <Star className="text-yellow-400" fill="#facc15" size={20} />
                  <Star className="text-yellow-400" fill="#facc15" size={20} />
                  <Star className="text-yellow-400" fill="#facc15" size={20} />
                  <Star className="text-yellow-400" fill="#facc15" size={20} />
                  <Star className="text-yellow-400" fill="#facc15" size={20} />
                </div>
                <p className="text-gray-700 leading-relaxed flex-1">
                  &quot;{displayText}&quot;
                </p>
                {isLong && (
                  <button
                    onClick={() => {
                      // Lendo um depoimento aberto, o carrossel fica parado
                      if (isExpanded) retomarDepois(); else pausar();
                      setExpandedIndex(isExpanded ? null : index);
                    }}
                    className="mt-2 text-sm text-[#2a3c5c] font-medium hover:underline text-left"
                  >
                    {isExpanded ? 'Ver menos' : 'Ver mais'}
                  </button>
                )}
                <div className="mt-5 pt-4 border-t border-[#e4e9f3]">
                  <p className="font-semibold text-[#2a3c5c]">{testimonial.name}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
        <button
          onClick={() => rolar(-1)}
          aria-label="Depoimento anterior"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2a3c5c] rounded-full p-2 shadow-md max-md:opacity-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={() => rolar(1)}
          aria-label="Próximo depoimento"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#2a3c5c] rounded-full p-2 shadow-md max-md:opacity-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        >
          <ChevronRight size={22} />
        </button>
        </div>
        <div className="text-center mt-6 flex flex-col items-center gap-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <a
              href={GOOGLE_AVALIACOES}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#2a3c5c] font-medium underline underline-offset-4 hover:text-[#1f2f4c]"
            >
              Ver todas as avaliações no Google
            </a>
            <a
              href={GOOGLE_AVALIAR}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#2a3c5c] font-medium underline underline-offset-4 hover:text-[#1f2f4c]"
            >
              Me avalie no Google
            </a>
          </div>
          <a
            href="#contato"
            className="inline-flex items-center justify-center rounded-lg bg-[#2a3c5c] px-7 py-3 text-white font-semibold transition duration-300 hover:bg-[#1f2f4c]"
          >
            Quero começar meu acompanhamento
          </a>
        </div>
      </div>
    </motion.section>
  );
};

export default TestimonialsSection;
